package fr.frux.shared.domain.model

import fr.frux.core.maps.Location
import kotlin.math.roundToInt

/**
 * Règle de proximité anti-doublon (#395, ADR 0025 lot 3), partagée par la carte et l'assistant de
 * création. La proximité propose, elle ne décide jamais : elle désigne des candidats, le choix
 * reste à l'utilisateur.
 *
 * Seuils à caler sur le terrain : un candidat de la même espèce (même
 * [rollupId][Species.rollupId]) compte jusqu'à 15 m, un candidat d'une autre espèce (cas du même
 * arbre mal identifié) seulement jusqu'à 5 m.
 */
const val DUPLICATE_SAME_SPECIES_RADIUS_METERS = 15.0
const val DUPLICATE_OTHER_SPECIES_RADIUS_METERS = 5.0

/** Au plus 3 candidats affichés, les plus proches d'abord. */
const val DUPLICATE_MAX_CANDIDATES = 3

/** Un candidat de doublon retenu, avec sa distance déjà calculée. */
data class DuplicateCandidate(
    val tree: Tree,
    val distanceMeters: Double,
) {
    /** Distance affichée, arrondie au mètre et jamais nulle (« à 0 m » ne dit rien à l'utilisateur). */
    val roundedMeters: Int get() = distanceMeters.roundToInt().coerceAtLeast(1)
}

/**
 * Filtre pur [trees] en candidats de doublon autour de [reference]. Avec [selectedSpeciesId] nul
 * (espèce pas encore choisie), tout arbre à 15 m ou moins est retenu. Sinon [rollupIdOf] résout le
 * `rollupId` d'une espèce depuis le référentiel chargé par l'écran, et s'il ne résout ni pour le
 * candidat ni pour l'espèce sélectionnée l'égalité retombe sur l'identifiant d'espèce plutôt que
 * d'échouer.
 *
 * Rend au plus [DUPLICATE_MAX_CANDIDATES] candidats triés par distance croissante.
 */
fun findDuplicateCandidates(
    trees: List<Tree>,
    reference: Location,
    selectedSpeciesId: Long?,
    rollupIdOf: (Long) -> Long?,
): List<DuplicateCandidate> {
    val selectedRollupId = selectedSpeciesId?.let(rollupIdOf)
    return trees
        .mapNotNull { tree ->
            val distance = tree.distanceMetersFrom(reference) ?: return@mapNotNull null
            val threshold =
                if (
                    selectedSpeciesId == null ||
                    isSameSpecies(tree.species.id, selectedSpeciesId, selectedRollupId, rollupIdOf)
                ) {
                    DUPLICATE_SAME_SPECIES_RADIUS_METERS
                } else {
                    DUPLICATE_OTHER_SPECIES_RADIUS_METERS
                }
            if (distance <= threshold) DuplicateCandidate(tree, distance) else null
        }.sortedBy { it.distanceMeters }
        .take(DUPLICATE_MAX_CANDIDATES)
}

private fun isSameSpecies(
    candidateSpeciesId: Long,
    selectedSpeciesId: Long,
    selectedRollupId: Long?,
    rollupIdOf: (Long) -> Long?,
): Boolean =
    candidateSpeciesId == selectedSpeciesId ||
        (selectedRollupId != null && rollupIdOf(candidateSpeciesId) == selectedRollupId)
